import React, { useState } from 'react';
import { useTranslate, crudGetOne } from 'react-admin';
import {
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@material-ui/core';
import cx from 'classnames';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
// import BuildIcon from '@material-ui/icons/Build';
import {
  H2,
  StatusField,
  TableCell,
  TextField as TextFieldComponent,
  ReferenceField,
} from '~/components';
import { Section } from '~/layout';
import { makeLabel } from './IntegrationTab';
import useStyles from './useStyles';
import { HeadTransaction } from '~/types/HeadTransaction';
import { Longdash, time } from '~/utils';
import { RejectionReason, IdentityType } from '~/types/TransactionsHistory';
import { useDispatch } from 'react-redux';
import { queryClient } from '~/App';
import { useIssueResolution } from '~/hooks/headTransactions';
import { mapIdentityTypeToObjectType } from '~/utils/profileType';
import { usePermissions } from 'react-admin';

// New interface, needs to be moved to types
interface TransactionIssue {
  id?: string;
  type?: string;
  code?: string;
  message?: string;
  details?: Record<string, any>;
  resolution?: {
    message?: string;
    resolvedAt?: string;
    resolvedByIdentityId?: string;
    resolvedByIdentityType?: IdentityType;
  };
}

// Handle both new 'issues' and legacy 'rejectionReason'
const getIssues = (trx: HeadTransaction): TransactionIssue[] => {
  // New 'issues'
  if (trx.details.issues && Array.isArray(trx.details.issues)) {
    return trx.details.issues;
  }

  // Legacy 'rejectionReason'
  if (trx.details.rejectionReason) {
    const rejectionReasons: RejectionReason[] =
      typeof trx.details.rejectionReason === 'string'
        ? [
            {
              code: '',
              type: '',
              message: trx.details.rejectionReason,
            },
          ]
        : trx.details.rejectionReason;

    // Convert legacy format to new format
    return rejectionReasons.map((reason) => ({
      type: reason.type,
      code: reason.code,
      message: reason.message,
    }));
  }

  return [];
};

const TransactionIssuesSection = (props: { trx: HeadTransaction }) => {
  const { trx } = props;
  const [selectedIssue, setSelectedIssue] = useState<TransactionIssue | null>(null);
  const [resolutionDialog, setResolutionDialog] = useState(false);
  const [resolutionMessage, setResolutionMessage] = useState<string | undefined>(undefined);
  const [viewResolutionDialog, setViewResolutionDialog] = useState(false);

  const dispatch = useDispatch();
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(makeLabel(`transactionIssues.${key}`));

  // Backward compatibility
  const tLegacy = (key: string) => translate(makeLabel(`rejectionReasons.${key}`));

  // Check if transaction is not older than 6 months
  const isTransactionEditable = () => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return new Date(trx.created) > sixMonthsAgo;
  };

  // Use new translations, fallback to legacy ones
  const getTranslation = (key: string) => {
    const newTranslation = t(key);
    return newTranslation !== makeLabel(`transactionIssues.${key}`) ? newTranslation : tLegacy(key);
  };

  const handleResolveIssue = (issue: TransactionIssue) => {
    setSelectedIssue(issue);
    setResolutionMessage(issue.resolution?.message || undefined);
    setResolutionDialog(true);
  };

  const handleViewResolution = (issue: TransactionIssue) => {
    setSelectedIssue(issue);
    setViewResolutionDialog(true);
  };

  const handleCloseDialogs = () => {
    setResolutionDialog(false);
    setViewResolutionDialog(false);
    setSelectedIssue(null);
    setResolutionMessage(undefined);
  };

  const { mutateAsync: saveIssueResolution } = useIssueResolution(() => {
    handleCloseDialogs();
    dispatch(crudGetOne('headTransactions', trx.id, '/headTransactions'));
    queryClient.invalidateQueries(['headTransactions', trx.id]);
  });

  const handleSaveResolution = async () => {
    if (!selectedIssue || !resolutionMessage) return;
    if (!trx?.id || !selectedIssue.id) return;

    saveIssueResolution({
      id: trx.id,
      issueId: selectedIssue.id,
      comment: resolutionMessage,
    });
  };

  const issues = getIssues(trx);

  const validateResolutionMessage = (value?: string) => {
    const isEmpty = value !== undefined && !value.trim();
    return {
      error: isEmpty,
      errorMessage: isEmpty ? t('resolutionMessageRequired') : undefined,
    };
  };

  const resolutionMessageState = validateResolutionMessage(resolutionMessage);

  const identityType = mapIdentityTypeToObjectType(
    selectedIssue?.resolution?.resolvedByIdentityType
  );
  return (
    <>
      <H2 topSpacing={0} bottomSpacing={2}>
        {getTranslation('_')}
      </H2>
      <Section className={`${classes.bottomSpacing}`}>
        <Grid container item xs={12}>
          <Table className={cx(classes.responsesTable, { [classes.autoWidth]: true })}>
            <TableHead>
              <TableRow>
                <TableCell width='40%'>{getTranslation('type')}</TableCell>
                <TableCell width='30%'>{getTranslation('code')}</TableCell>
                <TableCell width='30%'>{getTranslation('message')}</TableCell>
                <TableCell width='10%'>{getTranslation('status')}</TableCell>
                <TableCell style={{ width: '70px' }} align='right'>
                  {getTranslation('actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.length > 0 ? (
                issues.map((issue, index) => (
                  <Row
                    key={issue.id || index}
                    issue={issue}
                    isEditable={isTransactionEditable()}
                    onResolve={() => handleResolveIssue(issue)}
                    onViewResolution={() => handleViewResolution(issue)}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center', height: '2.5em' }}>
                    {getTranslation('noReasons')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
      </Section>

      {/* Resolution Dialog */}
      {/* only show when selectedIssue is truthy to prevent showing an empty dialog */}
      {selectedIssue && (
        <Dialog open={resolutionDialog} onClose={handleCloseDialogs} maxWidth='sm' fullWidth>
          <DialogTitle>
            {selectedIssue?.resolution ? t('editResolution') : t('resolveIssue')}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin='dense'
              label={t('resolutionMessage')}
              fullWidth
              multiline
              rows={4}
              value={resolutionMessage}
              onChange={(e) => setResolutionMessage(e.target.value)}
              required
              error={resolutionMessageState.error}
              helperText={resolutionMessageState.errorMessage}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs} color='default'>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSaveResolution}
              color='primary'
              disabled={resolutionMessageState.error || !selectedIssue?.id}
            >
              {t('save')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* View Resolution Dialog */}
      {selectedIssue && (
        <Dialog open={viewResolutionDialog} onClose={handleCloseDialogs} maxWidth='sm' fullWidth>
          <DialogTitle>{t('resolutionDetails')}</DialogTitle>
          <DialogContent>
            {selectedIssue?.resolution && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextFieldComponent label={t('resolutionMessage')} fullWidth multiline>
                    {selectedIssue.resolution?.message}
                  </TextFieldComponent>
                </Grid>
                <Grid item xs={12}>
                  <TextFieldComponent label={t('resolvedAt')}>
                    {selectedIssue.resolution?.resolvedAt
                      ? time(selectedIssue.resolution?.resolvedAt).format('lll')
                      : Longdash}
                  </TextFieldComponent>
                </Grid>
                <Grid item xs={12}>
                  {selectedIssue.resolution?.resolvedByIdentityId &&
                  selectedIssue.resolution?.resolvedByIdentityType &&
                  identityType ? (
                    <ReferenceField
                      label={t('resolvedBy')}
                      record={{
                        id: selectedIssue.resolution.resolvedByIdentityId,
                        type: identityType,
                      }}
                    />
                  ) : (
                    <TextFieldComponent label={t('resolvedBy')}>
                      {`${selectedIssue.resolution?.resolvedByIdentityType} (${selectedIssue.resolution?.resolvedByIdentityId})`}
                    </TextFieldComponent>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs} color='primary'>
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

interface Props {
  issue: TransactionIssue;
  isEditable: boolean;
  onResolve: () => void;
  onViewResolution: () => void;
}

const Row = (props: Props) => {
  const { issue, isEditable, onResolve, onViewResolution } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(makeLabel(`transactionIssues.${key}`));
  const { permissions } = usePermissions();
  const hasResolvePermission = permissions?.includes('headTransaction.issues.resolve');
  const isResolved = issue?.resolution && issue?.resolution?.resolvedAt;
  const hasId = Boolean(issue.id);
  return (
    <>
      <TableRow style={{ borderBottom: 0 }}>
        <TableCell style={{ overflowWrap: 'break-word' }}>{issue?.type || Longdash}</TableCell>
        <TableCell style={{ overflowWrap: 'break-word' }}>{issue?.code || Longdash}</TableCell>
        <TableCell style={{ overflowWrap: 'break-word' }}>{issue?.message || Longdash}</TableCell>
        <TableCell>
          <StatusField status={isResolved ? 'RESOLVED' : 'UNRESOLVED'} />
        </TableCell>
        <TableCell align='right'>
          <>
            {isResolved ? (
              <>
                <Tooltip title={t('viewResolution')}>
                  <IconButton size='small' onClick={onViewResolution}>
                    <VisibilityIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                {isEditable && hasId && hasResolvePermission && (
                  <Tooltip title={t('editResolution')}>
                    <IconButton size='small' onClick={onResolve}>
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              isEditable &&
              hasResolvePermission &&
              hasId && (
                <Tooltip title={t('resolveIssue')}>
                  <IconButton size='small' onClick={onResolve} color='primary'>
                    <EditIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )
            )}
          </>
        </TableCell>
      </TableRow>
    </>
  );
};

export default TransactionIssuesSection;
